/**
 * Site Management field sanitizers and validators
 * (NHS number, ODS code, contact details, UK postcode)
 */

const NHS_WEIGHTS = [10, 9, 8, 7, 6, 5, 4, 3, 2];

/** Strip spaces/hyphens; keep digits only (max 10). */
export const sanitizeNhsNumberInput = (value = "") =>
  String(value ?? "")
    .replace(/[\s-]/g, "")
    .replace(/\D/g, "")
    .slice(0, 10);

/** Display as XXX XXX XXXX while typing. */
export const formatNhsNumberDisplay = (value = "") => {
  const digits = sanitizeNhsNumberInput(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

/**
 * NHS Number Modulus 11 check.
 * Returns { valid, message, digits }.
 */
export const validateNhsNumber = (value) => {
  const digits = sanitizeNhsNumberInput(value);

  if (!digits) {
    return { valid: false, message: "NHS number is required.", digits };
  }
  if (!/^\d{10}$/.test(digits)) {
    return {
      valid: false,
      message: "NHS number must be exactly 10 digits.",
      digits,
    };
  }

  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    sum += Number(digits[i]) * NHS_WEIGHTS[i];
  }

  const remainder = sum % 11;
  let checkDigit = 11 - remainder;
  if (checkDigit === 11) checkDigit = 0;
  if (checkDigit === 10) {
    return {
      valid: false,
      message: "NHS number failed checksum verification.",
      digits,
    };
  }

  if (checkDigit !== Number(digits[9])) {
    return {
      valid: false,
      message: "NHS number failed checksum verification.",
      digits,
    };
  }

  return { valid: true, message: "", digits };
};

/** ODS: uppercase A-Z / 0-9 only, max 8. */
export const sanitizeOdsCodeInput = (value = "") =>
  String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);

/**
 * ODS code: 3–8 alphanumeric, no spaces.
 * Returns { valid, message, code }.
 */
export const validateOdsCode = (value, { required = true } = {}) => {
  const code = sanitizeOdsCodeInput(value);

  if (!code) {
    return {
      valid: !required,
      message: required ? "ODS code is required." : "",
      code,
    };
  }
  if (!/^[A-Z0-9]{3,8}$/.test(code)) {
    return {
      valid: false,
      message: "ODS code must be 3–8 letters or numbers (A–Z, 0–9).",
      code,
    };
  }

  return { valid: true, message: "", code };
};

/** Letters, spaces, apostrophes, hyphens, periods. */
export const validateContactName = (value, { required = true } = {}) => {
  const name = String(value ?? "").trim();

  if (!name) {
    return {
      valid: !required,
      message: required ? "Contact name is required." : "",
      name,
    };
  }
  if (name.length > 150) {
    return { valid: false, message: "Maximum 150 characters allowed.", name };
  }
  if (!/^[A-Za-z][A-Za-z\s'.-]*[A-Za-z.]$|^[A-Za-z]$/.test(name)) {
    return {
      valid: false,
      message:
        "Contact name may only contain letters, spaces, hyphens, apostrophes, and periods.",
      name,
    };
  }

  return { valid: true, message: "", name };
};

const DIAL_BY_COUNTRY = {
  US: "1",
  CA: "1",
  DE: "49",
  JP: "81",
  GB: "44",
  AU: "61",
  CH: "41",
  CN: "86",
  HK: "852",
  SG: "65",
  SE: "46",
  KR: "82",
  NO: "47",
  NZ: "64",
  MX: "52",
  IN: "91",
  BR: "55",
  ZA: "27",
  TR: "90",
  DK: "45",
};

/** National mobile length rules (after trunk 0 / dial strip). */
export const NATIONAL_PHONE_RULES = {
  GB: { min: 10, max: 10, mustStartWith: "7", label: "UK" },
  US: { min: 10, max: 10, label: "US" },
  CA: { min: 10, max: 10, label: "Canadian" },
  IN: { min: 10, max: 10, label: "Indian" },
  MX: { min: 10, max: 10, label: "Mexican" },
  TR: { min: 10, max: 10, label: "Turkish" },
  JP: { min: 10, max: 10, label: "Japanese" },
  AU: { min: 9, max: 9, label: "Australian" },
  CH: { min: 9, max: 9, label: "Swiss" },
  SE: { min: 9, max: 9, label: "Swedish" },
  ZA: { min: 9, max: 9, label: "South African" },
  HK: { min: 8, max: 8, label: "Hong Kong" },
  SG: { min: 8, max: 8, label: "Singapore" },
  NO: { min: 8, max: 8, label: "Norwegian" },
  DK: { min: 8, max: 8, label: "Danish" },
  CN: { min: 11, max: 11, label: "Chinese" },
  DE: { min: 10, max: 11, label: "German" },
  BR: { min: 10, max: 11, label: "Brazilian" },
  KR: { min: 9, max: 10, label: "South Korean" },
  NZ: { min: 8, max: 10, label: "New Zealand" },
};

const normalizeCountryIso2 = (countryIso2) =>
  String(countryIso2 || "GB")
    .trim()
    .toUpperCase() || "GB";

const rulesForCountry = (countryIso2) => {
  const iso = normalizeCountryIso2(countryIso2);
  return NATIONAL_PHONE_RULES[iso] || NATIONAL_PHONE_RULES.GB;
};

const dialDigitsForCountry = (countryIso2) =>
  DIAL_BY_COUNTRY[normalizeCountryIso2(countryIso2)] || "44";

/**
 * Digits-only national mobile for org country.
 * Strips trunk 0, org dial (and +dial on paste), caps at country max.
 * @returns {{ national: string, wrongDial: boolean }}
 */
export const normalizeNationalMobileInput = (value, countryIso2 = "GB") => {
  const iso = normalizeCountryIso2(countryIso2);
  const dialDigits = dialDigitsForCountry(iso);
  const rules = rulesForCountry(iso);
  const raw = String(value ?? "").trim();
  const hadPlus = raw.startsWith("+");
  let digits = raw.replace(/\D/g, "");

  if (!digits) {
    return { national: "", wrongDial: false };
  }

  if (hadPlus) {
    if (!digits.startsWith(dialDigits)) {
      return { national: "", wrongDial: true };
    }
    digits = digits.slice(dialDigits.length);
  } else if (iso === "US" || iso === "CA") {
    // NANP: 11 digits starting with 1 → drop country code
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.slice(1);
    }
  } else if (
    digits.startsWith(dialDigits) &&
    digits.length >= dialDigits.length + rules.min
  ) {
    digits = digits.slice(dialDigits.length);
  }

  while (digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return {
    national: digits.slice(0, rules.max),
    wrongDial: false,
  };
};

/** Digits-only national input (for onChange). Wrong dial → empty string. */
export const sanitizeNationalMobileInput = (value = "", countryIso2 = "GB") =>
  normalizeNationalMobileInput(value, countryIso2).national;

/**
 * Validate national mobile for organisation country (DB stores national only).
 * @returns {{ valid: boolean, message: string, national: string, wrongDial?: boolean }}
 */
export const validateNationalMobile = (value, options = {}) => {
  const required = options.required !== false;
  const countryIso2 = normalizeCountryIso2(options.countryIso2);
  const dialDigits = dialDigitsForCountry(countryIso2);
  const rules = rulesForCountry(countryIso2);
  const raw = String(value ?? "").trim();

  if (!raw) {
    return {
      valid: !required,
      message: required ? "Mobile number is required." : "",
      national: "",
    };
  }

  const { national, wrongDial } = normalizeNationalMobileInput(raw, countryIso2);
  if (wrongDial) {
    return {
      valid: false,
      message: `Mobile number must use the organisation country dial code (+${dialDigits}).`,
      national: "",
      wrongDial: true,
    };
  }

  if (!national) {
    return {
      valid: false,
      message: "Enter a valid mobile number.",
      national: "",
    };
  }

  if (national.length < rules.min || national.length > rules.max) {
    const lengthHint =
      rules.min === rules.max
        ? `${rules.min} digits`
        : `${rules.min}–${rules.max} digits`;
    return {
      valid: false,
      message: `Enter a valid ${rules.label} mobile number (${lengthHint}).`,
      national,
    };
  }

  if (rules.mustStartWith && !national.startsWith(rules.mustStartWith)) {
    return {
      valid: false,
      message: `Enter a valid ${rules.label} mobile number (${rules.min} digits starting with ${rules.mustStartWith}).`,
      national,
    };
  }

  return { valid: true, message: "", national };
};

/**
 * Legacy helper: validate then return E.164 (org dial + national).
 * Prefer validateNationalMobile + national DB storage for Participant Manager.
 */
export const validateContactPhone = (value, options = {}) => {
  const result = validateNationalMobile(value, options);
  const dialDigits = dialDigitsForCountry(options.countryIso2);
  if (!result.valid) {
    return {
      valid: false,
      message: result.message,
      phone: String(value ?? "").trim(),
      e164: "",
      national: result.national || "",
    };
  }
  if (!result.national) {
    return {
      valid: true,
      message: "",
      phone: "",
      e164: "",
      national: "",
    };
  }
  const e164 = `+${dialDigits}${result.national}`;
  return {
    valid: true,
    message: "",
    phone: e164,
    e164,
    national: result.national,
  };
};

/** Dial display helper, e.g. +44 */
export const dialCodeForCountry = (countryIso2 = "GB") =>
  `+${dialDigitsForCountry(countryIso2)}`;

/** Disabled prefix label for Participant Manager, e.g. "GB +44" */
export const orgMobileDialLabel = (countryIso2 = "GB") => {
  const iso = normalizeCountryIso2(countryIso2);
  return `${iso} ${dialCodeForCountry(iso)}`;
};

/** Keep phone formatting chars; cap at 15 digits (Site Management / legacy). */
export const sanitizeContactPhoneInput = (value = "") => {
  let digitCount = 0;
  let result = "";
  for (const ch of String(value ?? "")) {
    if (/\d/.test(ch)) {
      if (digitCount >= 15) continue;
      digitCount += 1;
      result += ch;
      continue;
    }
    if (/[+\s().-]/.test(ch)) {
      if (!result && ch !== "+") continue;
      result += ch;
    }
  }
  return result;
};

const EMAIL_REGEX =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export const validateContactEmail = (value, { required = true } = {}) => {
  const email = String(value ?? "").trim();

  if (!email) {
    return {
      valid: !required,
      message: required ? "Email is required." : "",
      email,
    };
  }
  if (email.length > 200) {
    return { valid: false, message: "Maximum 200 characters allowed.", email };
  }
  if (!EMAIL_REGEX.test(email)) {
    return { valid: false, message: "Enter a valid email address.", email };
  }

  return { valid: true, message: "", email };
};

/** UK postcode (outward + inward), optional single space. */
const UK_POSTCODE_REGEX =
  /^([A-Z]{1,2}\d[A-Z\d]?)\s?(\d[A-Z]{2})$/i;

export const sanitizePostcodeInput = (value = "") =>
  String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, "")
    .slice(0, 8);

export const normalizeUkPostcode = (value = "") => {
  const raw = String(value ?? "")
    .toUpperCase()
    .replace(/\s+/g, "");
  const match = raw.match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/);
  if (!match) return String(value ?? "").trim().toUpperCase();
  return `${match[1]} ${match[2]}`;
};

export const validateUkPostcode = (value, { required = true } = {}) => {
  const trimmed = String(value ?? "").trim().toUpperCase();

  if (!trimmed) {
    return {
      valid: !required,
      message: required ? "Postcode is required." : "",
      postcode: "",
    };
  }
  if (trimmed.length > 20) {
    return {
      valid: false,
      message: "Maximum 20 characters allowed.",
      postcode: trimmed,
    };
  }
  if (!UK_POSTCODE_REGEX.test(trimmed)) {
    return {
      valid: false,
      message: "Enter a valid UK postcode (e.g. SW1A 1AA).",
      postcode: trimmed,
    };
  }

  return {
    valid: true,
    message: "",
    postcode: normalizeUkPostcode(trimmed),
  };
};

/**
 * UK National Insurance number — same rules as legacy frmvalidate.NIvalidation.
 * Returns { valid, message, ni }. Empty is valid (optional field).
 */
export const validateNiNumber = (value, { required = false } = {}) => {
  const ni = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

  if (!ni) {
    return required
      ? { valid: false, message: "NI number is required.", ni: "" }
      : { valid: true, message: "", ni: "" };
  }

  // Match legacy frmvalidate.NIvalidation (first + second letter sets differ).
  // Q is not allowed in either position — so QQ… test numbers are invalid.
  const pattern =
    /^[A-CEGHJ-NOPR-TW-Z][A-CEGHJ-NPR-TW-Z]\d{6}[A-D]$/i;
  const bannedPrefix = /^(GB|BG|NK|KN|TN|NT|ZZ)/i;
  if (pattern.test(ni) && !bannedPrefix.test(ni)) {
    return { valid: true, message: "", ni };
  }

  return {
    valid: false,
    message: "Please enter a valid NI number.",
    ni,
  };
};

/** Optional NHS: empty OK; when present must pass Modulus 11. */
export const validateOptionalNhsNumber = (value) => {
  const digits = sanitizeNhsNumberInput(value);
  if (!digits) return { valid: true, message: "", digits: "" };
  return validateNhsNumber(digits);
};
